/* eslint-disable import/no-cycle */
import React from "react"
import styled, { ThemeProvider } from "styled-components"

import { useComponentizedPage } from "store/contexts/componentized-page"
import componentThemeExtension from "utils/componentThemeExtension"
import { getViewport } from "utils"

import ImageGallery from "components/image-gallery"
import GridGallery from "components/grid-gallery"
import VideoGallery from "components/video-gallery"
import TestimonialGallery from "components/testimonial-gallery"
import PeopleGallery from "components/people-gallery"
import ArticleGallery from "components/article-gallery"
import AnimatedSlider from "components/grid-gallery/animated-slider"
import ContentfulImage from "./image"
import ContentfulVideo from "./video"
import ContentfulTestimonialSlide from "./testimonial-slide"
import ContentfulTestimonialCard from "./testimonial"
import ContentfulButton from "./button"

const ContentfulCarousel = ({
  component,
  elRef,
  onTop,
  nested,
  parentLayout,
  isFocusLayout,
  leftFocus,
  isFocusedItem,
  isRailItem,
  isParentLayoutContainer,
}) => {
  const {
    items,
    heading,
    subheading,
    navigationAnchor,
    paginationType,
    id,
    hideOnPageLoad,
    button,
    theme,
    backgroundColor: carouselBackgroundColor,
    tracking,
    trackingId,
  } = component

  // unknown item type?
  if (!items || !items.length) return null

  const { backgroundColor } = items[0]

  const itemType = items[0].internal.type

  const title = heading && heading.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")

  const { isHomePage } = useComponentizedPage()

  return (
    <ThemeProvider
      theme={componentThemeExtension(
        itemType === "ContentfulGridLayout" ? carouselBackgroundColor : backgroundColor,
        nested,
        onTop,
        theme,
      )}
    >
      {(() => {
        switch (itemType) {
          case "ContentfulGridLayout": {
            if (isHomePage) {
              if (isParentLayoutContainer) {
                return (
                  <AnimatedSlider
                    gridData={items[0]}
                    autoPlay
                    autoPlayInterval={4000}
                    hideOnPageLoad={hideOnPageLoad}
                    dataId={id}
                    id={navigationAnchor && navigationAnchor.anchorId}
                    elRef={elRef}
                    title={!nested && title}
                    subtitle={subheading && subheading.childMarkdownRemark.html}
                    isFocusedItem={isFocusedItem}
                  />
                )
              }

              return (
                <ArticleGallery
                  items={items[0].items}
                  pageType="Landing"
                  tracking={tracking}
                  trackingId={trackingId}
                />
              )
            }

            return (
              <GridGallery
                hideOnPageLoad={hideOnPageLoad}
                dataId={id}
                id={navigationAnchor && navigationAnchor.anchorId}
                elRef={elRef}
                title={!nested && title}
                subtitle={subheading && subheading.childMarkdownRemark.html}
                gridData={items[0]}
                isFocusLayout={isFocusLayout}
                leftFocus={leftFocus}
                isFocusedItem={isFocusedItem}
                isRailItem={isRailItem}
                parentLayout={parentLayout}
              />
            )
          }

          case "ContentfulImage": {
            return (
              <ImageGallery
                hideOnPageLoad={hideOnPageLoad}
                dataId={id}
                id={navigationAnchor && navigationAnchor.anchorId}
                elRef={elRef}
                title={!nested && title}
                subtitle={subheading && subheading.childMarkdownRemark.html}
                images={items.map((item) => {
                  /**
                   * Nested image carousels use 1:1 aspect ratios for images.
                   */
                  if (nested) {
                    return (
                      <ContentfulImage
                        key={item.id}
                        onTop={onTop}
                        component={{
                          ...item,
                          image: [{ ...item.image[0], width: 1400, height: 1400 }],
                          useOriginalAspectRatio: true,
                        }}
                        nested
                        crop
                      />
                    )
                  }

                  // standalone carousel;
                  // find the shortest image and use its aspect ratio
                  const ratios = items.map((it) => it.image[0].height / it.image[0].width)
                  const minRatio = Math.min(...ratios)
                  const idxShortest = ratios.findIndex((r) => Math.abs(r - minRatio) < 1e-9)
                  const itemShortest = items[idxShortest]

                  return (
                    <ContentfulImage
                      key={item.id}
                      onTop={onTop}
                      component={{
                        ...item,
                        image: [
                          {
                            ...item.image[0],
                            width: itemShortest.image[0].width,
                            height: itemShortest.image[0].height,
                          },
                        ],
                        position: "Full-Width",
                        useOriginalAspectRatio: itemShortest.height < 750,
                      }}
                    />
                  )
                })}
              />
            )
          }

          case "ContentfulVideo":
            return (
              <VideoGallery
                hideOnPageLoad={hideOnPageLoad}
                dataId={id}
                id={navigationAnchor && navigationAnchor.anchorId}
                elRef={elRef}
                title={!nested && title}
                subtitle={subheading && subheading.childMarkdownRemark.html}
                videos={items.map((item) => (
                  <ContentfulVideo key={item.id} component={item} aspectRatio={434 / 771} nested />
                ))}
                videoMeta={items.map((item) => {
                  const itemTitle =
                    item.title &&
                    item.title.childMarkdownRemark.html.replace(/^<p>(.*)<\/p>$/i, "$1")
                  const description = item.description && item.description.childMarkdownRemark.html
                  return { title: itemTitle, description }
                })}
              />
            )

          case "ContentfulTestimonial":
            return (
              <TestimonialGallery
                hideOnPageLoad={hideOnPageLoad}
                dataId={id}
                id={navigationAnchor && navigationAnchor.anchorId}
                elRef={elRef}
                testimonials={items.map((item) =>
                  getViewport() === "mobile" ? (
                    <ContentfulTestimonialCard
                      key={item.id}
                      component={item}
                      onTop={onTop}
                      nested
                      standalone
                    />
                  ) : (
                    <ContentfulTestimonialSlide
                      key={item.id}
                      component={item}
                      onTop={onTop}
                      nested
                    />
                  ),
                )}
              />
            )

          case "ContentfulPerson":
            return (
              <PeopleGallery
                hideOnPageLoad={hideOnPageLoad}
                dataId={id}
                id={navigationAnchor && navigationAnchor.anchorId}
                elRef={elRef}
                people={items.map((item) => ({
                  image: item.image[0].secure_url,
                  title: item.personTitle,
                  name: item.name,
                  shortBio: item.shortBio?.raw,
                }))}
                thumbNav={paginationType === "Thumbnails"}
              />
            )

          default:
            return null
        }
      })()}
      {button && (
        <ButtonContainer>
          <ContentfulButton component={button} />
        </ButtonContainer>
      )}
    </ThemeProvider>
  )
}

const ButtonContainer = styled.div`
  padding-bottom: 5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({ theme }) => theme.component.background};
`

export default ContentfulCarousel
